import { useEffect, useState } from "react";
import { ChevronRight, CheckCircle, XCircle, HelpCircle, ArrowRight, Tent, ShieldCheck } from "lucide-react";
import { ZeltCard } from "@/components/ZeltCard";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSeo, generateBreadcrumbSchema } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";
import { getKategorieSeite } from "@/data/kategorieSeiten";
import type { Zelt } from "@/data/zelte";

interface KategorieSeiteProps {
  slug: string;
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  vergleichIds: string[];
}

function TextBlock({ text }: { text: string }) {
  const paragraphs = text.split("\n\n").filter(Boolean);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
          {p}
        </p>
      ))}
    </>
  );
}

export function KategorieSeite({ slug, onNavigate, onToggleVergleich, vergleichIds }: KategorieSeiteProps) {
  const [zelte, setZelte] = useState<Zelt[]>([]);
  const [loading, setLoading] = useState(true);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const seite = getKategorieSeite(slug);

  useEffect(() => {
    if (!seite) return;
    setLoading(true);

    const params = new URLSearchParams();
    if (seite.kategorie) params.set("kategorie", seite.kategorie);
    if (seite.filterOverrides?.personen) params.set("personen", seite.filterOverrides.personen.toString());
    if (seite.filterOverrides?.saison) params.set("saison", seite.filterOverrides.saison.toString());
    if (seite.filterOverrides?.tags?.length) params.set("tags", seite.filterOverrides.tags.join(","));
    if (seite.filterOverrides?.maxPreis) params.set("maxPreis", seite.filterOverrides.maxPreis.toString());
    params.set("sortBy", "bewertung");
    params.set("order", "desc");

    fetch(`/api/zelte?${params.toString()}`)
      .then(res => res.json())
      .then(data => {
        setZelte(data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, seite]);

  useSeo({
    title: seite?.seoTitle || `${slug} | zelt.kaufen`,
    description: seite?.seoDescription || "",
    keywords: seite?.seoKeywords || "",
    type: "website",
    schema: seite ? generateBreadcrumbSchema([
      { name: "Startseite", url: window.location.origin },
      { name: seite.title, url: `${window.location.origin}/${slug}` },
    ]) : undefined,
  });

  if (!seite) {
    return (
      <Container className="py-16 text-center">
        <h1 className="text-4xl font-bold mb-4">Seite nicht gefunden</h1>
        <p className="text-muted-foreground mb-8">Diese Kategorie existiert nicht.</p>
        <Button onClick={() => onNavigate("/zelte")}>Alle Zelte ansehen</Button>
      </Container>
    );
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: seite.faq.map(f => ({
      "@type": "Question",
      name: f.frage,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.antwort,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-emerald-500/5" />
        <div className="absolute inset-0 mesh-gradient opacity-20" />

        <Container className="relative">
          <Breadcrumb
            items={[
              { label: "Zelte", href: "/zelte" },
              { label: seite.title },
            ]}
            className="mb-6"
          />

          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              {seite.title}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {seite.heroSubtitle}
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <Button
                size="lg"
                onClick={() => {
                  document.getElementById("produkte")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-xl gap-2"
              >
                {loading ? "Lädt..." : `${zelte.length} ${seite.title} ansehen`}
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => onNavigate("/vergleich")}
                className="rounded-xl"
              >
                Zelte vergleichen
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Intro Text */}
      <section className="py-12 md:py-16">
        <Container>
          <div className="max-w-3xl text-lg">
            <TextBlock text={seite.beschreibung} />
          </div>
        </Container>
      </section>

      {/* Products */}
      <section id="produkte" className="py-12 md:py-16 bg-muted/30">
        <Container>
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">
                {seite.title} im Vergleich
              </h2>
              <p className="text-muted-foreground mt-1">
                {loading ? "Lädt..." : `${zelte.length} Produkte gefunden`}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate(seite.kategorie ? `/zelte?kategorie=${seite.kategorie}` : "/zelte")}
              className="rounded-xl gap-2 group hidden sm:flex"
            >
              Alle Filter
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card overflow-hidden">
                  <div className="aspect-[4/3] bg-muted shimmer" />
                  <div className="p-5 space-y-4">
                    <div className="h-4 bg-muted rounded-full w-1/3 shimmer" />
                    <div className="h-6 bg-muted rounded-full w-3/4 shimmer" />
                    <div className="h-4 bg-muted rounded-full w-full shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : zelte.length === 0 ? (
            <div className="text-center py-16">
              <Tent className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Keine Produkte gefunden</h3>
              <p className="text-muted-foreground mb-6">
                Aktuell haben wir keine passenden Zelte in dieser Kategorie.
              </p>
              <Button onClick={() => onNavigate("/zelte")}>Alle Zelte ansehen</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {zelte.map((zelt, index) => (
                <div
                  key={zelt.id}
                  className="slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <ZeltCard
                    zelt={zelt}
                    onNavigate={onNavigate}
                    onToggleVergleich={onToggleVergleich}
                    isInVergleich={vergleichIds.includes(zelt.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Vorteile & Nachteile */}
      <section className="py-12 md:py-16">
        <Container>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Vorteile & Nachteile
          </h2>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-green-200 dark:border-green-900/50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  Vorteile
                </h3>
                <ul className="space-y-3">
                  {seite.vorteile.map((v, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-200 dark:border-red-900/50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-red-700 dark:text-red-400">
                  <XCircle className="w-5 h-5" />
                  Nachteile
                </h3>
                <ul className="space-y-3">
                  {seite.nachteile.map((n, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <XCircle className="w-4 h-4 text-red-600 dark:text-red-500 mt-0.5 flex-shrink-0" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* Kaufberatung */}
      <section className="py-12 md:py-16 bg-muted/30">
        <Container>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
              <ShieldCheck className="w-4 h-4" />
              Kaufberatung
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              {seite.title} - Worauf Sie achten sollten
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {seite.kaufberatung.map((item, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
                <HelpCircle className="w-4 h-4" />
                FAQ
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                Häufig gestellte Fragen
              </h2>
            </div>

            <div className="space-y-3">
              {seite.faq.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "border rounded-xl overflow-hidden transition-all duration-300",
                    faqOpen === index ? "bg-muted/30 shadow-sm" : "bg-card hover:bg-muted/10"
                  )}
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    aria-expanded={faqOpen === index}
                    aria-controls={`faq-answer-${index}`}
                    className="w-full text-left p-4 md:p-5 flex items-start justify-between gap-4"
                  >
                    <span className="font-medium text-sm md:text-base">{item.frage}</span>
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 mt-1 flex-shrink-0 text-muted-foreground transition-transform duration-300",
                        faqOpen === index && "rotate-90"
                      )}
                    />
                  </button>
                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    className={cn(
                      "overflow-hidden transition-all duration-300",
                      faqOpen === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    )}
                  >
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.antwort}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-muted/30">
        <Container>
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-emerald-600" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15)_0%,transparent_50%)]" />

            <div className="relative p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Das richtige Zelt gefunden?
              </h2>
              <p className="text-white/80 max-w-xl mx-auto mb-8">
                Vergleichen Sie jetzt bis zu 4 Zelte nebeneinander und finden Sie Ihren Favoriten.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  onClick={() => onNavigate("/vergleich")}
                  className="rounded-xl bg-white text-primary hover:bg-white/90"
                >
                  Jetzt vergleichen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => onNavigate("/zelte")}
                  className="rounded-xl bg-transparent border-white/30 text-white hover:bg-white/10"
                >
                  Alle Zelte ansehen
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
